import React, { useEffect, useState } from "react";
import { collection, getDocs, deleteDoc, doc, updateDoc, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { getAuth, signOut } from 'firebase/auth'; // ✅ Import Firebase logout
import logo from "../assets/logo1.png";
import "../styles/Results.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaCalendarAlt,
  FaClock,
  FaUser,
  FaMapMarkerAlt,
  FaGlobeAmericas,
  FaFlag,
  FaBug,
  FaExclamationTriangle,
} from "react-icons/fa";

const PEST_FEATURES = {
  "Fall Armyworm": [
    "Brownish body with darker patches",
    "Distinct inverted Y-shaped marking on head",
    "Four dark spots forming a square on the  abdominal segment",
  ],
  "Cutworm": [
    "Gray or brown coloration of body",
    "Fat and curled body when touched",
    "No distinct stripes or spots",
  ],
  "Onion Armyworm": [
    "Typically dull green color of the body",
    "Lacks prominent hairs or spines in body",
    "Dark or black spot located on each side of the body behind the head",
  ]
};

function Results() {
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [pestFilter, setPestFilter] = useState("");
  const [lightboxImage, setLightboxImage] = useState(null);
  const [selectedReports, setSelectedReports] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [modalType, setModalType] = useState(null);
  const [modalMessage, setModalMessage] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false); // ✅ added for delete confirmation
  const [showLogoutModal, setShowLogoutModal] = useState(false); // ✅ logout modal
  const [checkedFeatures, setCheckedFeatures] = useState([]);
  const reportsPerPage = 8;
  const location = useLocation();
  const navigate = useNavigate();
  const auth = getAuth();

  // Trigger logout confirmation modal
  const confirmLogout = () => setShowLogoutModal(true);

  // ✅ Logout handler that works with protected routes
  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('user'); // optional: clear user data
      navigate('/login'); // redirect to login page
    } catch (error) {
      console.error('Logout error:', error);
    }
  };


  useEffect(() => {
    fetchReports();
  }, []);

useEffect(() => {
  if (location.state?.reportId && reports.length > 0) {
    const reportToOpen = reports.find((r) => r.id === location.state.reportId);

    if (reportToOpen) {
      openModal(reportToOpen);

      // 🧹 Clear the state so modal won't reopen on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }
}, [location.state, reports, navigate, location.pathname]);

  const fetchReports = async () => {
    try {
      const q = query(collection(db, "pestScans"), orderBy("date", "desc"));
      const snapshot = await getDocs(q);

      const fetchedReports = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          locationAddress: data.locationAddress || "Unknown",
          details: data.details || {},
          viewed: data.viewed || false,
        };
      });

      setReports(fetchedReports);
    } catch (error) {
      console.error("Error fetching reports:", error);
    }
  };

  const openModal = async (report) => {
    setSelectedReport(report);
    if (report.result && PEST_FEATURES[report.result]) {
      setCheckedFeatures(new Array(PEST_FEATURES[report.result].length).fill(true));
    } else {
      setCheckedFeatures([]);
    }

    if (!report.viewed) {
      try {
        await updateDoc(doc(db, "pestScans", report.id), { viewed: true });
        setReports((prev) =>
          prev.map((r) => (r.id === report.id ? { ...r, viewed: true } : r))
        );
      } catch (err) {
        console.error("Error updating viewed status:", err);
      }
    }
  };

  const closeModal = () => setSelectedReport(null);

  const showConfirmModal = (type, message, action) => {
    setModalType(type);
    setModalMessage(message);
    setConfirmAction(() => () => {
      action();
      closeConfirmModal();
    });
  };

  const closeConfirmModal = () => {
    setModalType(null);
    setModalMessage("");
    setConfirmAction(null);
  };

  const deleteReport = async (id) => {
    try {
      await deleteDoc(doc(db, "pestScans", id));
      setReports((prev) => prev.filter((r) => r.id !== id));
      closeModal();
    } catch (error) {
      console.error("Error deleting report:", error);
    }
  };

  const deleteSelectedReports = async () => {
    if (selectedReports.length === 0) return;
    showConfirmModal(
      "deleteBulk",
      `Delete ${selectedReports.length} selected report(s)?`,
      async () => {
        try {
          for (const id of selectedReports) {
            await deleteDoc(doc(db, "pestScans", id));
          }
          setReports((prev) => prev.filter((r) => !selectedReports.includes(r.id)));
          setSelectedReports([]);
        } catch (error) {
          console.error("Error deleting selected reports:", error);
        }
      }
    );
  };

  const updateMultipleVerificationStatus = (newStatus) => {
    if (selectedReports.length === 0) return;
    const capitalizedStatus = newStatus.charAt(0).toUpperCase() + newStatus.slice(1);
    showConfirmModal(
      "verifyBulk",
      `Mark ${selectedReports.length} selected report(s) as ${capitalizedStatus}?`,
      async () => {
        try {
          for (const id of selectedReports) {
            await updateDoc(doc(db, "pestScans", id), { verificationStatus: capitalizedStatus });
          }
          setReports((prev) =>
            prev.map((r) =>
              selectedReports.includes(r.id)
                ? { ...r, verificationStatus: capitalizedStatus }
                : r
            )
          );
          setSelectedReports([]);
        } catch (error) {
          console.error(`Error updating status to ${capitalizedStatus}:`, error);
        }
      }
    );
  };

  const changeVerificationStatus = async (id, newStatus) => {
    try {
      const docRef = doc(db, "pestScans", id);
      await updateDoc(docRef, { verificationStatus: newStatus });
      setReports((prev) =>
        prev.map((r) => (r.id === id ? { ...r, verificationStatus: newStatus } : r))
      );
      setSelectedReport((prev) => ({
        ...prev,
        verificationStatus: newStatus,
      }));
    } catch (error) {
      console.error("Error updating verification status:", error);
    }
  };

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") setLightboxImage(null);
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

const filteredReports = reports.filter((r) => {
  if (r.result === "Unidentified") return false; // ⛔ remove unidentified completely

  const matchesPest = pestFilter ? r.result === pestFilter : true;
  const matchesStatus = statusFilter ? r.verificationStatus === statusFilter : true;
  return matchesPest && matchesStatus;
});

  const indexOfLastReport = currentPage * reportsPerPage;
  const indexOfFirstReport = indexOfLastReport - reportsPerPage;
  const currentReports = filteredReports.slice(indexOfFirstReport, indexOfLastReport);
  const totalPages = Math.ceil(filteredReports.length / reportsPerPage);

  return (
    <div className="results-page">
      <header className="header-bar">
        <img src={logo} className="header-logo" alt="OnionMap" />
        <nav className="menu">
          <ul className="menu-list">
            <li><Link to="/home">HOME</Link></li>
            <li><Link to="/manage">MANAGE</Link></li>
            <li><Link to="/about-us">ABOUT US</Link></li>
            <li>
              <Link
                to="#"
                onClick={confirmLogout}
                className="logout-link"
              >
                LOGOUT
              </Link>
            </li>
          </ul>
        </nav>
      </header>

      <h2 className="results-title">Verify Reports</h2>

      <div className="filters sticky-filters">
        <select value={pestFilter} onChange={(e) => setPestFilter(e.target.value)} className="pest-select">
          <option value="">All Pests</option>
          <option value="Cutworm">Cutworm</option>
          <option value="Fall Armyworm">Fall Armyworm</option>
          <option value="Onion Armyworm">Onion Armyworm</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          <option value="Verified">Verified</option>
          <option value="Pending">Pending</option>
        </select>

        <div className="bulk-buttons">
          <button
            className="verify-btn"
            disabled={selectedReports.length === 0}
            onClick={() => updateMultipleVerificationStatus("verified")}
          >
            ✔ Verify ({selectedReports.length})
          </button>
          <button
            className="delete-btn"
            disabled={selectedReports.length === 0}
            onClick={deleteSelectedReports}
          >
            🗑 Delete ({selectedReports.length})
          </button>
        </div>
      </div>

      <div className="reports-grid">
        {currentReports.map((report) => (
          <div
            key={report.id}
            className={`report-card ${selectedReports.includes(report.id) ? "selected" : ""}`}
          >
            <input
              type="checkbox"
              checked={selectedReports.includes(report.id)}
              onChange={(e) => {
                e.stopPropagation();
                if (e.target.checked) {
                  setSelectedReports((prev) => [...prev, report.id]);
                } else {
                  setSelectedReports((prev) => prev.filter((id) => id !== report.id));
                }
              }}
              className="select-checkbox"
            />
            <div onClick={() => openModal(report)}>
              {report.image && (
                <div className="report-card-image-container">
                  <img src={report.image} alt="Pest" className="report-card-image" />
                  <div className="hover-overlay">
                    <span>👁 View Details</span>
                  </div>
                </div>
              )}
              <div className="report-header">
                <h3>{report.result || report.title || "Unknown Pest"}</h3>
                <p>{report.date}</p>
                <p>Reported by: {report.farmerName}</p>
              </div>

              <span className={`verification-status ${report.verificationStatus?.toLowerCase()}`}>
                {report.verificationStatus}
              </span>

              {report.viewed ? (
                <span className="label-viewed">VIEWED</span>
              ) : (
                <span className="label-new">NEW</span>
              )}
            </div>
          </div>
        ))}
      </div>

    {totalPages > 1 && (
      <div className="pagination">
        {/* ⟵ Prev Button */}
        <button
          className="page-nav"
          onClick={() => {
            if (currentPage > 1) {
              setCurrentPage(currentPage - 1);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
          disabled={currentPage === 1}
        >
          ⟵ Prev
        </button>

        {/* Numbered Buttons */}
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
          <button
            key={page}
            onClick={() => {
              setCurrentPage(page);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className={`page-number ${currentPage === page ? "active" : ""}`}
          >
            {page}
          </button>
        ))}

        {/* ⟶ Next Button */}
        <button
          className="page-nav"
          onClick={() => {
            if (currentPage < totalPages) {
              setCurrentPage(currentPage + 1);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
          disabled={currentPage === totalPages}
        >
          Next ⟶
        </button>
      </div>
    )}

    {selectedReport && (
      <div className="modal-overlay" onClick={closeModal}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <button className="modal-close" onClick={closeModal}>×</button>

          <div className="modal-header">
            <h2>{selectedReport.result || selectedReport.title || "Unknown Pest"}</h2>
          </div>

          <div className="modal-body">
            <p><FaCalendarAlt /> <strong>Date:</strong> {selectedReport.date}</p>
            <p><FaClock /> <strong>Time:</strong> {selectedReport.time}</p>
            <p><FaUser /> <strong>Farmer:</strong> {selectedReport.farmerName}</p>
            <p><FaMapMarkerAlt /> <strong>Location:</strong> {selectedReport.locationAddress}</p>
            <p><FaGlobeAmericas /> <strong>Latitude:</strong> {selectedReport.latitude}</p>
            <p><FaFlag /> <strong>Longitude:</strong> {selectedReport.longitude}</p>

            {selectedReport.details && (
              <>
                {selectedReport.result !== "Others" && selectedReport.result !== "Unidentified" ? (
                  <>
                    <p><FaBug /> <strong>Species:</strong> {selectedReport.details.species || "N/A"}</p>
                    <p><FaBug /> <strong>Family:</strong> {selectedReport.details.family || "N/A"}</p>
                    <p><FaBug /> <strong>Order:</strong> {selectedReport.details.order || "N/A"}</p>
                  </>
                ) : (
                  <p><FaBug /> <strong>Details:</strong> Unidentified Pest</p>
                )}

                {/* Damage Characteristics */}
                {selectedReport.details.damageCharacteristics && (
                  <>
                    <p><FaExclamationTriangle /> <strong>Damage Characteristics:</strong></p>
                    {Array.isArray(selectedReport.details.damageCharacteristics) ? (
                      <ul style={{ margin: 0, paddingLeft: "20px" }}>
                        {selectedReport.details.damageCharacteristics.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <p>{selectedReport.details.damageCharacteristics}</p>
                    )}
                  </>
                )}

                {/* Distinguishing Features */}
                {selectedReport.result && PEST_FEATURES[selectedReport.result] && (
                  <div className="feature-checklist-box">
                    <p><FaBug /> <strong>Distinct Appearance Features:</strong></p>
                    <ul className="checklist">
                      {PEST_FEATURES[selectedReport.result].map((feat, idx) => (
                        <li key={idx}>
                          <input
                            type="checkbox"
                            checked={checkedFeatures[idx]}
                            onChange={() => {
                              const newChecked = [...checkedFeatures];
                              newChecked[idx] = !newChecked[idx];
                              setCheckedFeatures(newChecked);
                            }}
                          />
                          <span>{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}

            {/* IMAGE */}
            {selectedReport.image && (
              <img
                src={selectedReport.image}
                alt="Pest"
                className="report-image"
                onClick={() => setLightboxImage(selectedReport.image)}
                style={{ cursor: "zoom-in" }}
              />
            )}
          </div>

          {/* MODAL ACTIONS */}
          <div className="modal-actions">
            <button
              className="delete-btn"
              disabled={selectedReport.verificationStatus === "Verified"}
              onClick={() =>
                showConfirmModal(
                  "verifySingle",
                  "Are you sure you want to verify this report?",
                  async () => {
                    await changeVerificationStatus(selectedReport.id, "Verified");
                  }
                )
              }
            >
              {selectedReport.verificationStatus === "Verified" ? "Verified" : "Verify Report"}
            </button>

            <button className="delete-btn" onClick={() => setShowDeleteModal(true)}>
              Delete Report
            </button>
          </div>
        </div>
      </div>
    )}

            {/* ===== Logout Confirmation Modal ===== */}
      {showLogoutModal && (
        <div className="delete-confirm-modal">
          <div className="delete-confirm-content">
            <h3>Confirm Logout</h3>
            <p>Are you sure you want to log out of your account?</p>
            <div className="delete-confirm-buttons">
              <button className="delete-confirm-btn confirm" onClick={handleLogout}>
                Confirm
              </button>
              <button className="delete-confirm-btn cancel" onClick={() => setShowLogoutModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Delete Confirmation Modal (on top of pest modal) */}
      {showDeleteModal && (
        <div className="delete-confirm-modal">
          <div className="delete-confirm-content">
            <h3>Confirm Deletion</h3>
            <p>Are you sure you want to delete this report?</p>
            <div className="delete-confirm-buttons">
              <button
                className="delete-confirm-btn confirm"
                onClick={() => {
                  deleteReport(selectedReport.id);
                  setShowDeleteModal(false);
                }}
              >
                Yes, Delete
              </button>
              <button
                className="delete-confirm-btn cancel"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {lightboxImage && (
        <div className="lightbox-overlay" onClick={() => setLightboxImage(null)}>
          <img src={lightboxImage} alt="Zoomed" className="lightbox-image" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {modalType && (
        <div className="delete-confirm-modal">
          <div className="delete-confirm-content">
            <h3>Confirmation</h3>
            <p>{modalMessage}</p>
            <div className="delete-confirm-buttons">
              <button className="delete-confirm-btn confirm" onClick={confirmAction}>
                Confirm
              </button>
              <button className="delete-confirm-btn cancel" onClick={closeConfirmModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Results;
